import React from 'react';
import { useTranslation } from 'react-i18next';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { RadioCard, RadioCardGroup } from '../shared/RadioCardGroup';
import type { ConvertWizard } from './useConvertWizard';

interface ChooseTargetStepProps {
  wizard: ConvertWizard;
  drbfmItemName: string;
}

/** Radix SelectItem throws on value=""; the MUI "create new unit" option used it. */
const NEW_UNIT_VALUE = '__new_unit__';

const NewUnitFields: React.FC<ChooseTargetStepProps> = ({ wizard, drbfmItemName }) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4 sm:flex-row">
      <Field
        error={wizard.fieldErrors.newUnitNumber}
        label={t('drbfm.convert.unitNumber')}
        className="w-full sm:w-[120px]"
        required
      >
        {(control) => (
          <Input
            {...control}
            className="revy-figure"
            value={wizard.newUnitNumber}
            onChange={(e) => wizard.setNewUnitNumber(e.target.value)}
          />
        )}
      </Field>
      <Field
        error={wizard.fieldErrors.newUnitName}
        label={t('drbfm.convert.unitName')}
        className="flex-1"
        required
      >
        {(control) => (
          <Input
            {...control}
            value={wizard.newUnitName}
            onChange={(e) => wizard.setNewUnitName(e.target.value)}
            placeholder={drbfmItemName}
          />
        )}
      </Field>
    </div>
  );
};

const ChooseTargetStep: React.FC<ChooseTargetStepProps> = ({ wizard, drbfmItemName }) => {
  const { t } = useTranslation();
  const { selectedSession } = wizard;

  return (
    <RadioCardGroup
      value={wizard.targetMode}
      onValueChange={(value) => wizard.setTargetMode(value as 'existing' | 'new')}
    >
      <RadioCard value="existing" label={t('drbfm.convert.existingSession')} />
      {wizard.targetMode === 'existing' && (
        <div className="pb-4 pl-8">
          {wizard.sessionsLoading ? (
            <Spinner />
          ) : (
            <div className="flex flex-col gap-4">
              <Field label={t('drbfm.convert.verificationSession')}>
                {({ id }) => (
                  <Select
                    value={wizard.selectedSessionId}
                    onValueChange={wizard.handleSelectSession}
                  >
                    <SelectTrigger id={id}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="revy-ui">
                      {wizard.existingSessions.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          <span className="revy-cjk">
                            {s.title}
                            {s.modelName ? ` (${s.modelName})` : ''}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              {selectedSession && selectedSession.units.length > 0 && (
                <Field label={t('drbfm.convert.unit')}>
                  {({ id }) => (
                    <Select
                      value={wizard.selectedUnitId || NEW_UNIT_VALUE}
                      onValueChange={(value) =>
                        wizard.setSelectedUnitId(value === NEW_UNIT_VALUE ? '' : value)
                      }
                    >
                      <SelectTrigger id={id}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="revy-ui">
                        <SelectItem value={NEW_UNIT_VALUE}>
                          <span className="revy-cjk">{t('drbfm.convert.createNewUnit')}</span>
                        </SelectItem>
                        {selectedSession.units.map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            <span className="revy-cjk">
                              {u.unitNumber} {u.unitName}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>
              )}
              {wizard.isCreatingUnit && (
                <NewUnitFields wizard={wizard} drbfmItemName={drbfmItemName} />
              )}
            </div>
          )}
        </div>
      )}

      <RadioCard value="new" label={t('drbfm.convert.newSession')} />
      {wizard.targetMode === 'new' && (
        <div className="flex flex-col gap-4 pl-8">
          <Field error={wizard.fieldErrors.newSessionTitle} label={t('drbfm.convert.sessionTitle')} required>
            {(control) => (
              <Input
                {...control}
                value={wizard.newSessionTitle}
                onChange={(e) => wizard.setNewSessionTitle(e.target.value)}
              />
            )}
          </Field>
          <Field error={wizard.fieldErrors.newSessionModelName} label={t('drbfm.convert.modelName')}>
            {(control) => (
              <Input
                {...control}
                value={wizard.newModelName}
                onChange={(e) => wizard.setNewModelName(e.target.value)}
                placeholder={t('drbfm.convert.modelNamePlaceholder')}
              />
            )}
          </Field>
          <NewUnitFields wizard={wizard} drbfmItemName={drbfmItemName} />
        </div>
      )}
    </RadioCardGroup>
  );
};

export default ChooseTargetStep;
